import { CommonModule } from '@angular/common';
import { Inject, Injectable, NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsHmrLifeCycle, NgxsHmrSnapshot } from '@ngxs/hmr-plugin';
import { NgxsModule, NoopNgxsExecutionStrategy, StateContext } from '@ngxs/store';
import { RecaptchaSettings, RECAPTCHA_SETTINGS } from 'ng-recaptcha';
import { Observable } from 'rxjs';

import { RtAnalyticsModule } from '@recruitee/analytics';
import {
  AccountBase,
  APP_CONFIG,
  COMPANY_ID,
  LocalStorageKeyBuilder,
  RtcErrorHandlerModule,
} from '@recruitee/common';
import {
  AuthService,
  HttpApiErrorHandlerService,
  HttpApiService,
  HttpRequestData,
  RtHttpModule,
} from '@recruitee/http';
import { I18nModule, LANGUAGE_CONFIG, LOCALE } from '@recruitee/i18n';
import { RtPlatform, RtUIModule } from '@recruitee/user-interface';
import { ReferralsWebsocketService, ReferralsWebsocketServiceConfig } from '@recruitee/websocket';
import { RtWysiwygModule, WysiwygUploader } from '@recruitee/wysiwyg';

import { environment } from '../environments/environment';
import { authService } from '../main-deps';
import { AppComponent } from './app.component';
import { AppRevisionProvider, AppRevisionToken } from './app.revision';
import { appConfig, languageConfig } from './config';
import { ReferralsRequestData } from './config/referrals-request-data';
import { CoreModule } from './core/core.module';
import { ReferralsHttpApiErrorHandlerService } from './core/http-api-error-handler.service';
import { MainViewModule } from './core/main-view/main-view.module';
import { ReferralsRepository, REPOSITORIES } from './core/repository';
import { CoreState } from './core/state/core.state';
import { MainViewState } from './core/state/main-view/main-view.state';
import { RoutingModule } from './routing/routing.module';
import { LocalStorageKeyBuilderService } from './shared/local-storage-key-builder.service';
import { SharedModule } from './shared/shared.module';

@Injectable()
export class RtrWysiwygUploader implements WysiwygUploader {
  constructor(private httpApi: HttpApiService) {}

  public upload(file: File): Observable<{ fileUrl: string }> {
    const data = new FormData();
    data.append('upload[file]', file);
    return this.httpApi.post('/uploads', null, data);
  }
}

@Injectable()
export class Account extends AccountBase {}

export const rootState = [MainViewState, ReferralsRepository, CoreState, ...REPOSITORIES];

@NgModule({
  imports: [
    MainViewModule,
    BrowserModule,
    BrowserAnimationsModule,
    CommonModule,
    RoutingModule,
    CoreModule,
    SharedModule,
    ReactiveFormsModule,
    RtAnalyticsModule.forRoot({
      disable: !environment.analytics,
      segmentKey: environment.analyticsKey,
      trackRouteChange: true,
    }),
    RtHttpModule.forRoot({
      provide: HttpRequestData,
      useClass: ReferralsRequestData,
    }),
    RtUIModule.forRoot(),
    I18nModule.forPhraseApp({
      phraseAppProjectId: environment.phraseAppProjectId,
      editor: true,
    }),
    NgxsModule.forRoot(rootState, {
      developmentMode: !environment.production,
      executionStrategy: NoopNgxsExecutionStrategy,
      selectorOptions: {
        suppressErrors: false,
      },
    }),
    RtcErrorHandlerModule.forRoot(
      {
        enable: environment.production,
        throwOnErrors: !environment.production,
        dsn: environment.sentry_dsn,
        revisionToken: AppRevisionToken,
      },
      Account,
    ),
    RtWysiwygModule.forRoot({
      provide: WysiwygUploader,
      useClass: RtrWysiwygUploader,
    }),
    NgxsReduxDevtoolsPluginModule.forRoot({
      name: 'referrals',
      disabled: environment.production,
      maxAge: 50,
    }),
  ],
  declarations: [AppComponent],
  providers: [
    Account,
    AppRevisionProvider,
    { provide: COMPANY_ID, useValue: '' },
    { provide: LOCALE, useValue: languageConfig.defaultLanguage },
    { provide: APP_CONFIG, useValue: appConfig },
    { provide: LANGUAGE_CONFIG, useValue: languageConfig },
    {
      provide: RECAPTCHA_SETTINGS,
      useValue: <RecaptchaSettings>{ siteKey: appConfig.recaptchaSiteKey, size: 'invisible' },
    },
    {
      provide: ReferralsWebsocketService.config,
      useValue: <ReferralsWebsocketServiceConfig>{ websocketUrl: environment.websocketUrl },
    },
    {
      provide: HttpApiErrorHandlerService,
      useClass: ReferralsHttpApiErrorHandlerService,
      deps: [AuthService, [Inject, COMPANY_ID]],
    },
    {
      provide: AuthService,
      useValue: authService,
    },
    {
      provide: LocalStorageKeyBuilder,
      useExisting: LocalStorageKeyBuilderService,
    },
    RtPlatform,
  ],
  bootstrap: [AppComponent],
})
export class AppModule implements NgxsHmrLifeCycle<NgxsHmrSnapshot> {
  public hmrNgxsStoreOnInit(
    ctx: StateContext<NgxsHmrSnapshot>,
    snapshot: Partial<NgxsHmrSnapshot>,
  ): void {
    ctx.patchState(snapshot);
  }

  public hmrNgxsStoreBeforeOnDestroy(ctx: StateContext<NgxsHmrSnapshot>): Partial<NgxsHmrSnapshot> {
    return ctx.getState();
  }
}
